import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import { pagesText } from 'src/constants/pagesText';
import { useAddExistingPolicy, useManageBasePolicy } from 'src/hooks/environmentWdacPolicy';
import UseExistingPolicyForm from './UseExistingPolicyForm';

// The one way to bring a base policy under management, from the managed policies list or from a
// form generating against bases. Either way the policy is handed back through onDone; a form uses it
// to return with the policy selected.
const UseExistingPolicyModal = ({ open, onDone, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: manageBasePolicy, isLoading: managing } = useManageBasePolicy();
  const { mutateAsync: addExistingPolicy, isLoading: adding } = useAddExistingPolicy();

  // The api layer rejects with the backend's message as a plain string, and the rules behind
  // managing a base policy are worth showing verbatim.
  const showError = error =>
    enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
      variant: 'error',
      autoHideDuration: 5000,
    });

  const handleUseDetected = async (policy, settings) => {
    try {
      await manageBasePolicy({ envPolicyId: policy.id, payload: settings });
      enqueueSnackbar(`${policy.policyName} is now a managed base policy.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onDone(policy);
    } catch (error) {
      showError(error);
    }
  };

  const handleAddExisting = async payload => {
    try {
      const added = await addExistingPolicy(payload);
      enqueueSnackbar(`${payload.policyName} added as a managed base policy.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onDone(added);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="mdi:shield-search" width={22} height={22} color="#87C8E0" />
        Use Existing Base Policy
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            Bring a base policy under portal management: pick one your devices already report, or
            add one that exists in your organization but has not been detected yet.
          </Typography>
          <UseExistingPolicyForm
            loading={managing || adding}
            onCancel={onClose}
            onUseDetected={handleUseDetected}
            onAddExisting={handleAddExisting}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

UseExistingPolicyModal.propTypes = {
  open: PropTypes.bool,
  onDone: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UseExistingPolicyModal;
